import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { BlockContent } from '~/components/BlockContent'

import bootcampStyle from '~/shared/styles/BootcampSanityBlock.module.css'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ExclamationCircleIcon from '~/components/Icons/svgs/ExclamationCircleIcon'
import { MultipleChoiceOptionsLayout } from '~/types/bootcamp'

import { AnswerType, MultipleChoiceActivityData, MultipleChoiceProps } from './MultipleChoice.type'
import { checkAnswer, getFeedBackString, generateMultipleActivityData } from './MultipleChoice.util'
import BootcampMultipleChoiceOption, { BootcampMultipleChoiceOptionsWrapper } from './MultipleChoiceOption'

const emptyQuestion = {
  question: [],
  options: [],
  optionsLayout: MultipleChoiceOptionsLayout.twoColumn,
  questionId: '',
  showOptions: 0,
  maximumRightAnswer: 1,
  shouldShuffle: false,
  _key: '',
}

function MultipleChoice({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onCurrentQuestionChange,
  actionButtonsFC,
  questions,
}: MultipleChoiceProps): React.ReactElement {
  const [answer, setAnswer] = useState<boolean[]>([])
  const [feedback, setFeedback] = useState<AnswerType | null>(null)
  const [questionHistory, setQuestionHistory] = useState<number[]>([])
  const [activityData, setActivityData] = useState<MultipleChoiceActivityData>({
    question: emptyQuestion,
    totalRightAnswer: 1,
    questionIndex: 0,
  })
  const { t } = useTranslation('bootcamp')

  useEffect(() => {
    if (questionHistory.length && questionHistory.length >= questions.length) {
      setQuestionHistory([])
    } else {
      setActivityData(generateMultipleActivityData(questions, questionHistory))
      setAnswer([])
      setFeedback(null)
    }
  }, [questionHistory, questions])

  // Keyed on string derivatives: activityData.question is deep-cloned on every call. The displayed
  // order rides along only when actually shuffled - unshuffled, it is itself a good answer key.
  const displayedKeys = activityData.question.options.map((option) => option._key).join(' ')
  const wasShuffled = process.env.NEXT_PUBLIC_STOP_SHUFFLE !== 'true' && activityData.question.shouldShuffle
  useEffect(() => {
    if (!activityData.question.questionId) return
    onCurrentQuestionChange?.(
      activityData.question.questionId,
      wasShuffled && displayedKeys
        ? { displayedOptionKeys: displayedKeys.split(' '), optionsLayout: activityData.question.optionsLayout }
        : undefined,
    )
  }, [
    activityData.question.questionId,
    activityData.question.optionsLayout,
    displayedKeys,
    wasShuffled,
    onCurrentQuestionChange,
  ])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])
  const handleOnCheckAnswerClick = (): void => {
    const ans = checkAnswer(activityData.question, answer, activityData.totalRightAnswer)
    setFeedback(ans)
    // Every selected option's Sanity _key: multi-select is real, and the displayed order is shuffled.
    const optionKeys = activityData.question.options
      .filter((_option, index) => Boolean(answer[index]))
      .map((option) => option._key)
    onAnswerStateChange(ans === AnswerType.CORRECT, activityData.question.questionId, undefined, {
      type: 'multiple_choice',
      optionKeys,
    })
  }

  const handleOnNextQuestionClick = (): void => {
    setQuestionHistory((prevState) => {
      return [activityData.questionIndex, ...prevState].slice(0, questions.length - 1)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  const onOptionClick = useCallback(
    (index: number) => {
      setFeedback(null)
      if (activityData.totalRightAnswer > 1) {
        setAnswer((oldAnswer) => {
          const tempAnswer = JSON.parse(JSON.stringify(oldAnswer))
          tempAnswer[index] = !tempAnswer[index]
          return tempAnswer
        })
      } else {
        setAnswer((oldAnswer) => {
          if (oldAnswer[index] === true) return []
          else {
            const tempAnswer = []
            tempAnswer[index] = true
            return tempAnswer
          }
        })
      }
    },
    [activityData.totalRightAnswer],
  )

  const getFeedBack = (feedbackKey: string | null): React.ReactElement => {
    const message = getFeedBackString(feedbackKey, t)
    if (message.length === 0) return <></>
    return (
      <div className="w-full min-h-9 mb-2">
        {message.length > 0 && (
          <div className="flex items-center p-2 bg-purple text-white text-xs md:text-sm leading-4 font-normal text-center rounded-medium">
            <div className="mr-2">
              <span className="shadow-large">
                <SVGIcon icon={ExclamationCircleIcon} pathClassName="text-white" size={19} />
              </span>
            </div>
            {message}
          </div>
        )}
      </div>
    )
  }

  return (
    <ActivityWrapper>
      <div className="my-auto py-8 md:py-0 w-full flex-col flex-wrap justify-center items-center text-purple font-semibold text-md">
        <div className="text-center mt-4 mb-8">
          {activityData.question.question.length > 0 && (
            <BlockContent
              blocks={activityData.question.question}
              className={`${bootcampStyle['bootcamp-block-content']} h-full`}
              renderContainerOnSingleChild
            />
          )}
        </div>
        {getFeedBack(feedback)}
        <BootcampMultipleChoiceOptionsWrapper optionsLayout={activityData.question.optionsLayout}>
          <>
            {activityData.question.options.map((option, index) => (
              <BootcampMultipleChoiceOption
                index={index}
                isRight={option.isCorrect}
                isSelected={answer[index] === true}
                key={option._key}
                onClick={onOptionClick}
                option={option.option}
                revealAnswer={answerState === false && answer[index] === true}
                totalRightAnswer={activityData.totalRightAnswer}
              />
            ))}
          </>
        </BootcampMultipleChoiceOptionsWrapper>
      </div>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer.filter((a) => a).length > 0,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            setAnswer([])
            setFeedback(null)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default MultipleChoice
